import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PrimaryNavigation from '../../components/ui/PrimaryNavigation';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../auth/AuthProvider';
import { supabase } from '../../lib/supabaseClient';
import { useNavigate } from 'react-router-dom';

const NeighborsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [neighbors, setNeighbors] = useState([]);
  const [selectedNeighborId, setSelectedNeighborId] = useState('');

  const [activities, setActivities] = useState([]);
  const [isActivitiesLoading, setIsActivitiesLoading] = useState(false);
  const [isActivitiesLoadingMore, setIsActivitiesLoadingMore] = useState(false);
  const [hasMoreActivities, setHasMoreActivities] = useState(true);
  const activitiesObserverTarget = useRef(null);

  const NEIGHBORS_PAGE_SIZE = 50;
  const ACTIVITIES_PAGE_SIZE = 20;

  const normalizeAvatarUrl = useCallback((url) => {
    if (!url) return '';
    const idx = String(url).indexOf('?');
    if (idx === -1) return String(url);
    return String(url).slice(0, idx);
  }, []);

  const hasNew = useMemo(() => (neighbors || []).some((n) => n?.hasNew), [neighbors]);

  const loadNeighbors = useCallback(async () => {
    if (!user?.id) return;
    setIsLoading(true);
    try {
      const { data, error } = await supabase.rpc('ch_list_neighbors_with_activity_page', {
        p_limit: NEIGHBORS_PAGE_SIZE,
        p_offset: 0,
        p_window_hours: 24,
      });
      if (error) {
        setNeighbors([]);
        return;
      }
      setNeighbors(
        (data || []).map((x) => ({
          id: x.neighbor_id,
          username: x.username,
          avatarUrl: normalizeAvatarUrl(x.avatar_url || ''),
          hasNew: !!x.has_new,
        }))
      );
    } finally {
      setIsLoading(false);
    }
  }, [NEIGHBORS_PAGE_SIZE, normalizeAvatarUrl, user?.id]);

  const loadActivitiesFirstPage = useCallback(async () => {
    if (!user?.id) return;
    setIsActivitiesLoading(true);
    try {
      const { data, error } = await supabase.rpc('ch_list_neighbor_activities_page', {
        p_neighbor_id: selectedNeighborId || null,
        p_limit: ACTIVITIES_PAGE_SIZE,
        p_offset: 0,
      });
      if (error) {
        setActivities([]);
        setHasMoreActivities(false);
        return;
      }
      const mapped = (data || []).map((x) => ({
        neighborId: x.neighbor_id,
        neighborUsername: x.neighbor_username,
        neighborAvatarUrl: normalizeAvatarUrl(x.neighbor_avatar_url || ''),
        occurredAt: x.occurred_at,
        activityType: x.activity_type,
        activityTitle: x.activity_title,
        projectId: x.project_id,
        challengeId: x.challenge_id,
        proofId: x.proof_id,
      }));
      setActivities(mapped);
      setHasMoreActivities(mapped.length >= ACTIVITIES_PAGE_SIZE);
    } finally {
      setIsActivitiesLoading(false);
    }
  }, [ACTIVITIES_PAGE_SIZE, normalizeAvatarUrl, selectedNeighborId, user?.id]);

  const loadActivitiesMore = useCallback(async () => {
    if (!user?.id) return;
    if (isActivitiesLoadingMore || isActivitiesLoading || !hasMoreActivities) return;
    setIsActivitiesLoadingMore(true);
    try {
      const offset = activities?.length || 0;
      const { data, error } = await supabase.rpc('ch_list_neighbor_activities_page', {
        p_neighbor_id: selectedNeighborId || null,
        p_limit: ACTIVITIES_PAGE_SIZE,
        p_offset: offset,
      });
      if (error) {
        setHasMoreActivities(false);
        return;
      }
      const mapped = (data || []).map((x) => ({
        neighborId: x.neighbor_id,
        neighborUsername: x.neighbor_username,
        neighborAvatarUrl: x.neighbor_avatar_url || '',
        occurredAt: x.occurred_at,
        activityType: x.activity_type,
        activityTitle: x.activity_title,
        projectId: x.project_id,
        challengeId: x.challenge_id,
        proofId: x.proof_id,
      }));
      setActivities((prev) => [...(prev || []), ...mapped]);
      if (mapped.length < ACTIVITIES_PAGE_SIZE) setHasMoreActivities(false);
    } finally {
      setIsActivitiesLoadingMore(false);
    }
  }, [ACTIVITIES_PAGE_SIZE, activities?.length, hasMoreActivities, isActivitiesLoading, isActivitiesLoadingMore, normalizeAvatarUrl, selectedNeighborId, user?.id]);

  const selectedNeighbor = useMemo(() => {
    if (!selectedNeighborId) return null;
    return (neighbors || []).find((n) => n.id === selectedNeighborId) || null;
  }, [neighbors, selectedNeighborId]);

  useEffect(() => {
    loadNeighbors();
  }, [loadNeighbors]);

  useEffect(() => {
    if (!user?.id) return;
    supabase.rpc('ch_mark_neighbors_seen');
  }, [user?.id]);

  useEffect(() => {
    loadActivitiesFirstPage();
  }, [loadActivitiesFirstPage]);

  useEffect(() => {
    if (!hasMoreActivities) return;
    const el = activitiesObserverTarget?.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries?.[0]?.isIntersecting) {
          loadActivitiesMore();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMoreActivities, loadActivitiesMore]);

  return (
    <div className="min-h-screen bg-background pt-16">
      <PrimaryNavigation />
      <BreadcrumbNavigation />

      <main className="max-w-screen-2xl mx-auto px-4 sm:px-5 lg:px-8 py-5 md:py-8 lg:py-12">
        <div className="bg-card rounded-lg shadow-gaming-md p-5 md:p-6 mb-6">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">이웃</h1>
              <p className="text-sm text-muted-foreground caption mt-1">
                이웃들의 최근 활동을 확인하세요
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                loadNeighbors();
                loadActivitiesFirstPage();
              }}
              className="px-4 py-2 rounded-md bg-muted/40 text-foreground hover:bg-muted transition-gaming"
            >
              새로고침
            </button>
          </div>

          {hasNew ? (
            <div className="mt-4 text-xs text-muted-foreground caption">
              새 활동이 있는 이웃이 있습니다.
            </div>
          ) : null}
        </div>

        <div className="bg-card rounded-lg shadow-gaming-md p-4 md:p-5 mb-4 sticky top-16 z-10">
          <div className="flex items-center gap-3 overflow-x-auto">
            <button
              type="button"
              onClick={() => setSelectedNeighborId('')}
              className={`shrink-0 px-3 py-2 rounded-full border transition-gaming text-sm ${
                !selectedNeighborId
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-card text-foreground border-border hover:bg-muted/30'
              }`}
            >
              전체
            </button>
            {(neighbors || []).map((n) => (
              <button
                key={n.id}
                type="button"
                onClick={() => setSelectedNeighborId(n.id)}
                className={`shrink-0 flex items-center gap-2 px-3 py-2 rounded-full border transition-gaming ${
                  selectedNeighborId === n.id
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-card text-foreground border-border hover:bg-muted/30'
                }`}
              >
                <span className="relative w-7 h-7 rounded-full bg-muted overflow-hidden flex items-center justify-center border border-border">
                  {n.avatarUrl ? (
                    <img src={n.avatarUrl} alt={n.username} className="w-full h-full object-cover" />
                  ) : (
                    <Icon name="User" size={14} className="text-muted-foreground" />
                  )}
                  {n.hasNew ? (
                    <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-destructive border border-card" />
                  ) : null}
                </span>
                <span className="text-sm font-medium whitespace-nowrap">{n.username}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-card rounded-lg shadow-gaming-md p-5 md:p-6">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground">최근 활동</h2>
              <p className="text-sm text-muted-foreground caption mt-1">
                {selectedNeighborId ? '선택한 이웃의 활동' : '이웃 전체 활동'}
              </p>
            </div>
            {selectedNeighbor ? (
              <button
                type="button"
                onClick={() => navigate(`/people/user?id=${selectedNeighbor.id}`)}
                className="shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-md bg-muted/40 text-foreground hover:bg-muted transition-gaming"
              >
                <span className="w-6 h-6 rounded-full bg-muted overflow-hidden flex items-center justify-center border border-border">
                  {selectedNeighbor.avatarUrl ? (
                    <img
                      src={selectedNeighbor.avatarUrl}
                      alt={selectedNeighbor.username}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                  ) : (
                    <Icon name="User" size={14} className="text-muted-foreground" />
                  )}
                </span>
                <span className="text-sm font-medium">유저채널 방문</span>
              </button>
            ) : null}
          </div>

          <div className="mt-5 space-y-2">
            {isLoading || isActivitiesLoading ? (
              <div className="text-sm text-muted-foreground caption">로딩 중...</div>
            ) : (activities || []).length === 0 ? (
              <div className="text-sm text-muted-foreground caption">활동이 없습니다.</div>
            ) : (
              (activities || []).map((a, idx) => (
                <button
                  type="button"
                  key={`${a.neighborId}-${a.occurredAt}-${idx}`}
                  className="w-full text-left flex items-center justify-between gap-3 p-3 rounded-md border border-border bg-card hover:bg-muted/30 transition-gaming"
                  onClick={() => {
                    if (a.activityType === 'project') {
                      navigate(`/people/user?id=${a.neighborId}`);
                      return;
                    }
                    if (a.activityType === 'challenge' && a.projectId) {
                      navigate(`/project-detail?id=${a.projectId}`);
                      return;
                    }
                    if (a.activityType === 'proof') {
                      navigate(`/people/user?id=${a.neighborId}&tab=proofs`);
                      return;
                    }
                    navigate(`/people/user?id=${a.neighborId}`);
                  }}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-muted overflow-hidden flex items-center justify-center border border-border">
                      {a.neighborAvatarUrl ? (
                        <img
                          src={a.neighborAvatarUrl}
                          alt={a.neighborUsername}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        <Icon name="User" size={16} className="text-muted-foreground" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm text-foreground truncate">
                        <span className="font-medium">{a.neighborUsername}</span>
                        <span className="text-muted-foreground"> · </span>
                        {a.activityType === 'project' ? '프로젝트 등록' : null}
                        {a.activityType === 'challenge' ? '챌린지 등록' : null}
                        {a.activityType === 'proof' ? '인증 등록' : null}
                      </div>
                      {a.activityTitle ? (
                        <div className="text-xs text-muted-foreground caption mt-0.5 truncate">{a.activityTitle}</div>
                      ) : null}
                      {a.occurredAt ? (
                        <div className="text-xs text-muted-foreground caption mt-0.5">
                          {String(a.occurredAt).slice(0, 16).replace('T', ' ')}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <Icon name="ChevronRight" size={18} className="text-muted-foreground" />
                </button>
              ))
            )}
          </div>

          {hasMoreActivities ? <div ref={activitiesObserverTarget} className="h-10" /> : null}
          {isActivitiesLoadingMore ? (
            <div className="mt-3 text-sm text-muted-foreground caption">로딩 중...</div>
          ) : null}
        </div>
      </main>
    </div>
  );
};

export default NeighborsPage;
